import React, { useCallback, useEffect, useRef, useState } from 'react';
import './ChatAnimatedIcon.css';

const ACTIVE_COLOR = '#3262FF';
const INACTIVE_COLOR = '#282932';

function setStaticIconState(bubble, line1, line2) {
  if (!bubble || !line1 || !line2) return;
  bubble.style.strokeDasharray = 'none';
  bubble.style.strokeDashoffset = '0';
  bubble.style.transition = 'none';
  line1.style.transformOrigin = '9px 11px';
  line1.style.transform = 'scaleX(1)';
  line1.style.transition = 'none';
  line2.style.transformOrigin = '9px 15px';
  line2.style.transform = 'scaleX(1)';
  line2.style.transition = 'none';
}

function ChatAnimatedIcon({ color, isActive = false, size = 24 }) {
  const strokeColor = color || (isActive ? ACTIVE_COLOR : INACTIVE_COLOR);
  const bubbleRef = useRef(null);
  const line1Ref = useRef(null);
  const line2Ref = useRef(null);
  const [bouncing, setBouncing] = useState(false);
  const [animating, setAnimating] = useState(false);
  const timersRef = useRef([]);
  const wasActiveRef = useRef(isActive);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((id) => clearTimeout(id));
    timersRef.current = [];
  }, []);

  const playAnimation = useCallback(() => {
    const bubble = bubbleRef.current;
    const line1 = line1Ref.current;
    const line2 = line2Ref.current;
    if (!bubble || !line1 || !line2) return;

    clearTimers();
    setBouncing(false);
    setAnimating(true);

    const len = bubble.getTotalLength();
    bubble.style.strokeDasharray = `${len}`;
    bubble.style.strokeDashoffset = `${len}`;
    bubble.style.transition = 'none';

    line1.style.transformOrigin = '9px 11px';
    line1.style.transform = 'scaleX(0)';
    line1.style.transition = 'none';

    line2.style.transformOrigin = '9px 15px';
    line2.style.transform = 'scaleX(0)';
    line2.style.transition = 'none';

    requestAnimationFrame(() => {
      bubble.style.transition = 'stroke-dashoffset 350ms ease';
      bubble.style.strokeDashoffset = '0';

      timersRef.current.push(setTimeout(() => {
        line1.style.transition = 'transform 180ms ease-out';
        line1.style.transform = 'scaleX(1)';
      }, 300));

      timersRef.current.push(setTimeout(() => {
        line2.style.transition = 'transform 180ms ease-out';
        line2.style.transform = 'scaleX(1)';
      }, 450));

      timersRef.current.push(setTimeout(() => {
        setBouncing(true);
        timersRef.current.push(setTimeout(() => setBouncing(false), 350));
      }, 650));

      timersRef.current.push(setTimeout(() => {
        setAnimating(false);
      }, 1000));
    });
  }, [clearTimers]);

  useEffect(() => {
    if (isActive && !wasActiveRef.current) {
      playAnimation();
    } else if (!isActive) {
      setAnimating(false);
      requestAnimationFrame(() => {
        setStaticIconState(bubbleRef.current, line1Ref.current, line2Ref.current);
      });
    }
    wasActiveRef.current = isActive;
    return clearTimers;
  }, [isActive, playAnimation, clearTimers]);

  const handleClick = useCallback(() => {
    playAnimation();
  }, [playAnimation]);

  return (
    <span
      className={[
        'chat-animated-icon',
        isActive && !animating ? 'chat-animated-icon--active' : '',
      ].filter(Boolean).join(' ')}
      style={{ '--chat-icon-stroke': strokeColor, width: size, height: size }}
      onClick={handleClick}
      role="presentation"
    >
      <svg
        className={bouncing ? 'chat-animated-icon--bounce' : ''}
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          ref={bubbleRef}
          className="chat-icon-bubble"
          d="M12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22H4C3.46957 22 2.96086 21.7893 2.58579 21.4142C2.21071 21.0391 2 20.5304 2 20V12C2 6.477 6.477 2 12 2Z"
        />
        <line
          ref={line1Ref}
          className="chat-icon-line chat-icon-line1"
          x1="9"
          y1="11"
          x2="15"
          y2="11"
        />
        <line
          ref={line2Ref}
          className="chat-icon-line chat-icon-line2"
          x1="9"
          y1="15"
          x2="12"
          y2="15"
        />
      </svg>
    </span>
  );
}

export default ChatAnimatedIcon;
